import { useInView } from 'motion/react'
import { type CSSProperties, useRef } from 'react'
import { cn } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'
import { formatClock, useCountUp } from '#app/utils/site/scene-clock'
import type { Audience } from '#app/utils/site/site-links'
import {
	type TrackView,
	pinLeft,
	pinVisible,
	segmentDone,
	segmentProgress,
	trackTotal,
} from '#app/utils/site/track'
import { BigHeading, Container } from '../primitives'

const SEGMENT_COLOURS = [
	'bg-tt-ink',
	'bg-tt-purple',
	'bg-tt-mauve',
	'bg-tt-olive',
	'bg-[#747474]',
]

/**
 * Label colour per segment, for at least 4.5:1 (the labels are 11 to 17px
 * bold, never "large" text). White passes on ink (13.1:1) and the grey
 * (4.67:1) but not on purple (3.72), mauve (3.25) or olive (3.15). Ink
 * #32302f is not dark enough there either (3.52 / 4.04 / 4.17), so those
 * three use a warm near-black: 5.10 / 5.85 / 6.04.
 */
const SEGMENT_LABEL_COLOURS = [
	'text-white',
	'text-[#111010]',
	'text-[#111010]',
	'text-[#111010]',
	'text-white',
]

/** The whole track (300 s or 720 s) plays in this long. */
const PLAY_MS = 3600
/** The gap between the bar's segments; `pinLeft` needs the same number. */
const BAR_GAP_PX = 4

/**
 * The timeline track under the hero: a big clock and five proportional
 * segments. It plays once per mount, when 35% of it is in view: the clock
 * counts up with a cubic ease-out while each segment fills in turn and its
 * time label fades in once it is full. The landlord side also raises the
 * "You Approve" pin when the clock reaches it. Reduced motion shows the end
 * state straight away (`useCountUp` resolves to the total).
 */
export function TimelineTrack({ audience }: { audience: Audience }) {
	// Keyed so a swap between the tenant and landlord sides remounts the
	// track: the entrance replays and the clock counts up again instead of
	// inheriting the other side's finished count.
	return <Track key={audience} audience={audience} />
}

function Track({ audience }: { audience: Audience }) {
	const copy: TrackView = siteContent.track[audience]
	const total = trackTotal(copy.segments)
	const ref = useRef<HTMLElement>(null)
	const inView = useInView(ref, { once: true, amount: 0.35 })
	const { value: seconds } = useCountUp({
		active: inView,
		target: total,
		durationMs: PLAY_MS,
	})
	const progress = segmentProgress(seconds, copy.segments)
	const pin = copy.pin
	const showPin = pin ? pinVisible(seconds, pin.atSeconds) : false

	return (
		<section
			ref={ref}
			id="how"
			className="relative z-10 scroll-mt-16 border-b border-tt-line-soft bg-white pb-[72px] pt-10 sm:scroll-mt-[76px] sm:pb-[84px]"
			aria-labelledby="tt-track-heading"
		>
			<Container>
				<div className="tt-swap-in flex flex-wrap items-end justify-between gap-[30px]">
					<BigHeading
						as="h2"
						id="tt-track-heading"
						className="max-w-[16ch] text-[clamp(44px,5.4vw,80px)]"
					>
						{copy.heading.map((line) => (
							<span key={line} className="block">
								{line}
							</span>
						))}
					</BigHeading>
					<div className="text-right max-[760px]:text-left">
						<span
							className="tt-big tt-tabular inline-block min-w-[2.75em] text-right text-[clamp(72px,9vw,132px)] max-[760px]:text-left"
							aria-hidden="true"
						>
							{formatClock(seconds)}
						</span>
						<span className="sr-only">{formatClock(total)}</span>
						<div className="mt-1.5 text-[15px] text-tt-muted">{copy.unit}</div>
					</div>
				</div>

				<div className="relative mt-14 max-[520px]:mt-[84px]">
					{pin ? (
						<div
							className={cn(
								'absolute bottom-[calc(100%+12px)] flex -translate-x-1/2 flex-col items-center gap-2 transition-opacity duration-[350ms] ease-out',
								showPin ? 'opacity-100' : 'opacity-0',
							)}
							style={{
								left: pinLeft(copy.segments, pin.atSeconds, BAR_GAP_PX),
							}}
						>
							<b className="whitespace-nowrap rounded-full bg-tt-sage px-[13px] py-1.5 text-[13px] font-bold leading-[1.3]">
								{pin.label}
							</b>
							<i className="h-3.5 w-0.5 rounded-sm bg-tt-ink" />
						</div>
					) : null}

					<div className="flex h-16 gap-1 max-[760px]:h-12" aria-hidden="true">
						{copy.segments.map((segment, i) => {
							const done = segmentDone(progress[i] ?? 0)
							return (
								<div
									key={segment.name}
									style={{ '--tt-seg': segment.seconds } as CSSProperties}
									className="@container relative flex-(--tt-seg) overflow-hidden rounded-[14px] bg-tt-inset"
								>
									<i
										className={cn(
											'absolute inset-0 origin-left will-change-transform',
											SEGMENT_COLOURS[i],
										)}
										style={{ transform: `scaleX(${progress[i] ?? 0})` }}
									/>
									{/* A segment narrower than its label (the landlord's 1 min on
									    a 360px phone) hides the label instead of clipping it; the
									    row below still names the step with its colour square. The
									    10px step keeps "1 min 10 sec" inside its box at 320px. */}
									<span
										className={cn(
											'absolute inset-0 flex items-center justify-center whitespace-nowrap text-[clamp(12px,1.25vw,17px)] font-bold transition-opacity duration-[350ms] ease-out max-[520px]:text-[11px] max-[340px]:text-[10px] @max-[28px]:hidden',
											SEGMENT_LABEL_COLOURS[i],
											done ? 'opacity-100' : 'opacity-0',
										)}
									>
										{segment.time}
									</span>
								</div>
							)
						})}
					</div>

					<ol className="mt-4 flex gap-1 max-[760px]:flex-wrap max-[760px]:gap-x-3 max-[760px]:gap-y-3.5">
						{copy.segments.map((segment, i) => (
							<li
								key={segment.name}
								style={{ '--tt-seg': segment.seconds } as CSSProperties}
								className={cn(
									'flex-(--tt-seg) text-center text-[15px] font-semibold leading-[1.3]',
									'max-[760px]:flex max-[760px]:flex-[0_0_calc(50%-6px)] max-[760px]:items-center max-[760px]:gap-2 max-[760px]:text-left max-[760px]:text-[14px]',
								)}
							>
								<i
									className={cn(
										'hidden h-2.5 w-2.5 flex-none rounded-[3px] max-[760px]:block',
										SEGMENT_COLOURS[i],
									)}
								/>
								<span>
									{segment.nameMobile ? (
										<>
											{segment.nameMobile[0]}
											<br className="hidden max-[520px]:inline" />{' '}
											{segment.nameMobile[1]}
										</>
									) : (
										segment.name
									)}
									<span className="sr-only">, {segment.time}</span>
								</span>
							</li>
						))}
					</ol>
				</div>
			</Container>
		</section>
	)
}
